import React from 'react'
import {
    addDoc,
    collection,
    deleteDoc,
    doc,
    getDocs,
    getFirestore,
    limit,
    onSnapshot,
    query,
    setDoc,
    Unsubscribe,
    where
} from "firebase/firestore"
import {getAuth, signInAnonymously} from "firebase/auth"

import update from "immutability-helper"

import Session from "../interfaces/Session"
import {Alert, Button, Snackbar} from "@mui/material"

import {Player} from "../interfaces/Player"
import Song from "../interfaces/Song"
import {Vote} from "../interfaces/Vote"
import Playlist from "../interfaces/Playlist"
import SongList from "./SongList"
import {dispatchChange, setLoadingText, showLoading} from "../Utils"
import ReactDOM from "react-dom"


type Unsub = {
    session?: Unsubscribe
    song?: Unsubscribe
    playlist?: Unsubscribe
}

const Home = () => {

    const [account, setAccount] = React.useState<string | undefined>(undefined)
    const [session, setSession] = React.useState<Session | null | undefined>(undefined)
    const [player, setPlayer] = React.useState<Player | undefined>(undefined)
    const [songs, setSongs] = React.useState<Song[]>([])
    const [votes, setVotes] = React.useState<Vote[]>([])
    const [playlist, setPlaylist] = React.useState<Playlist | undefined>(undefined)

    const [unsub, setUnsub] = React.useState<Unsub>({})
    const [error, setError] = React.useState<string | undefined>(undefined)

    React.useEffect(() => {

        getDocs(query(collection(getFirestore(), '/account'), where("domain", 'array-contains', window.location.hostname), limit(1)))
            .then(querySnapshot => {
                if (!querySnapshot.empty) {
                    setAccount(querySnapshot.docs[0].id)
                }
            })
            .catch(err => {
                setError(err.message)
            })

        return () => {
            unsub.session && unsub.session()
            unsub.song && unsub.song()
            unsub.playlist && unsub.playlist()
        }

    }, [])

    React.useEffect(() => {

        if (account) {
            const unsubSession = onSnapshot(query(collection(getFirestore(), '/account/' + account + '/sessions'), where("closeDT", "==", null), limit(1)).withConverter(Session.converter), querySnapshot => {
                if (querySnapshot.empty) {
                    setSession(prev => {
                        if (prev) {
                            unparticipate()
                        }
                        return null
                    })

                    showLoading()
                    setLoadingText('aucune session actuellement')

                } else {
                    setLoadingText('')

                    setSession(prev => {
                        if (prev && prev.key !== querySnapshot.docs[0].id) {
                            unparticipate()
                        }
                        return querySnapshot.docs[0].data()
                    })
                }
            }, err => {
                setError(err.message)
            })

            setUnsub(prev => {
                prev.session && prev.session()
                return {...prev, ...{session: unsubSession}}
            })
        }

    }, [account])

    React.useEffect(() => {
        if (player) {
            setUnsub(prev => {
                prev.playlist && prev.playlist()

                if (session?.playlistKey) {
                    setPlaylist(new Playlist())
                    let unsubPlaylist = onSnapshot(doc(getFirestore(), '/account/' + account + '/playlists', session?.playlistKey).withConverter(Playlist.converter), querySnapshot => {
                        if (querySnapshot.exists()) {
                            setPlaylist(querySnapshot.data())
                        } else {
                            setPlaylist(undefined)
                        }
                    }, err => {
                        setError(err.message)
                    })
                    return {...prev, ...{playlist: unsubPlaylist}}

                } else {
                    setPlaylist(undefined)
                    return {...prev, ...{playlist: undefined}}
                }
            })
        }

    }, [account, player, session?.playlistKey])


    const songList = React.useMemo<Song[]>(() => {
        let list: Song[] = []

        if (session) {
            songs.forEach(song => {
                if (!song.hide && (!playlist || (song.key && playlist.hasSong(song)))) {
                    song.sessionVoted = (votes.find(vote => vote.songKey === song.key) !== undefined) ? 1 : 0
                    song.sessionPlayed = (session as Session).isSongPlayed(song)

                    list.push(song)
                }
            })
        }

        return list

    }, [songs, playlist, votes, session])

    const participate = async (participantUID: string) => {
        let player = new Player()

        player.key = participantUID
        player.sessionKey = session?.key
        player.dt = new Date()

        try {
            await setDoc(doc(getFirestore(), "/account/" + account + '/players/', participantUID).withConverter(Player.converter), player)
            setPlayer(player)
        } catch (err: any) {
            setError(err.message)
        }

        const unsubSongs = onSnapshot(collection(getFirestore(), '/account/' + account + '/songs').withConverter(Song.converter), querySnapshot => {
            querySnapshot.docChanges().forEach(change => {
                dispatchChange<Song>(setSongs, change)
            })
        }, err => {
            setError(err.message)
        })
        setUnsub(prev => {
            prev.song && prev.song()
            return {...prev, ...{song: unsubSongs}}
        })

        try {
            const snapshot = await getDocs(query(collection(getFirestore(), '/account/' + account + '/votes'), where('user', "==", participantUID), where('session', '==', session?.key)).withConverter(Vote.converter))
            snapshot.forEach(doc => {
                setVotes(prev =>
                    update(prev, {
                        $push: [doc.data()]
                    })
                )
            })
        } catch (err: any) {
            setError(err.message)
        }
    }

    const unparticipate = () => {

        setUnsub(prev => {
            if (prev.song) {
                prev.song()
            }
            if (prev.playlist) {
                prev.playlist()
            }

            return {song: undefined, playlist: undefined}
        })

        setPlayer(undefined)
    }

    const onParticipate = async () => {
        getAuth().onAuthStateChanged(async user => {
            if (!user) {
                signInAnonymously(getAuth())
                    .then(credential => participate(credential.user.uid))
                    .catch(err => {
                        setError(err.message)
                    })
            } else {
                await participate(user.uid)
            }
        })
    }

    const onVote = async (songKey: string) => {
        let voteIndex = votes.findIndex(vote => vote.songKey === songKey)
        if (voteIndex >= 0) { // unvote for song
            let voteKey: string | undefined = votes[voteIndex].key

            if (voteKey) {
                try {

                    await deleteDoc(doc(getFirestore(), '/account/' + account + '/votes', voteKey))
                    setVotes(prev => update(prev, {
                        $splice: [[voteIndex, 1]]
                    }))

                } catch (err: any) {
                    setError(err.message)
                }
            }

        } else {
            let song = songs.find(song => song.key === songKey)
            if (song) {
                let vote = new Vote()

                //vote.key = player?.key
                vote.userKey = player?.key
                vote.songKey = songKey
                vote.sessionKey = session?.key
                vote.dt = new Date()

                try {
                    const voteRef = await addDoc(collection(getFirestore(), '/account/' + account + '/votes'), Vote.converter.toFirestore(vote))
                    vote.key = voteRef.id

                    setVotes(prev => update(prev, {
                        $push: [vote]
                    }))

                } catch (err: any) {
                    setError(err.message)
                }

            } // else songKey doesn't exist
        }
    }

    return <div>
        {
            session ?
                !player ?
                    ReactDOM.createPortal(<Button onClick={onParticipate}
                                                  variant={"contained"}>Participer</Button>, document.getElementById('participate-button') as Element)
                    : <SongList session={session} songs={songList} onVote={onVote}/>
                : null
        }
        {
            error ?
                <Snackbar open={true} onClose={() => {
                    setError(undefined)
                }}>
                    <Alert severity="error">{error}</Alert>
                </Snackbar>
                : null
        }
    </div>
}

export default Home
